import { CellOfRow } from '@components/tables';
import { DayNightColorMode } from '@EcamModel/model/PTZCameraDayNight';
import {
    CorridorMode,
    DayNightMode,
    DoorMode,
    EnhancementMode,
    ExposureMode,
    ExposureTime,
    FocusMode,
    HighLightCompensationMode,
    ImageEnhancementMode,
    ImageRotation,
    PowerLineFrequency,
    PTZCameraSettingEnable,
    SmartIrMode,
    WhiteBalanceMode,
    ZoomLimit,
} from '@EcamModel/model/PTZCameraSetting';
import { Stack, TableBody, TableRow, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import {
    BaseSectionCollapse,
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
    TableHeaderStyle,
} from './BasicInfoMilesightCameraPTZ';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';
import { useEffect } from 'react';

const powerLineFrequencyMapping: { [key in PowerLineFrequency]: string } = {
    [PowerLineFrequency.SixtyHZ]: '60Hz',
    [PowerLineFrequency.FiftyHZ]: '50Hz',
};

const whiteBalanceModeMapping: { [key in WhiteBalanceMode]: string } = {
    [WhiteBalanceMode.Auto]: 'Auto white balance',
    [WhiteBalanceMode.Manual]: 'Manual white balance',
    [WhiteBalanceMode.Incandescent]: 'Incandescent lamp',
    [WhiteBalanceMode.Warm]: 'Warm light lamp',
    [WhiteBalanceMode.Natural]: 'Natural light',
    [WhiteBalanceMode.Flurescent]: 'Fluorescent lamp',
    [WhiteBalanceMode.Schedule]: 'Schedule',
};

const doorModeMapping: { [key in DoorMode]: string } = {
    [DoorMode.Outdoor]: 'Outdoor',
    [DoorMode.Indoor]: 'Indoor',
};

const irBlanceModeMapping: { [key in PTZCameraSettingEnable]: string } = {
    [PTZCameraSettingEnable.Off]: 'Off',
    [PTZCameraSettingEnable.On]: 'On',
};

const dayColorModeMapping: { [key in DayNightColorMode]: string } = {
    [DayNightColorMode.BWMode]: 'B/W',
    [DayNightColorMode.ColorMode]: 'Color',
};

export const exposureModeMapping: { [key in ExposureMode]: string } = {
    [ExposureMode.Auto]: 'Auto',
    [ExposureMode.Manual]: 'Manual',
    [ExposureMode.Schedule]: 'Schedule',
};

export const exposureTimeMapping: { [key in ExposureTime]: string } = {
    [ExposureTime.OneFifth]: '1/5',
    [ExposureTime.OneTenth]: '1/10',
    [ExposureTime.OneTwentyFifthOrOneThirtieth]: '1/25 or 1/30',
    [ExposureTime.OneFiftiethOrOneSixtieth]: '1/50 or 1/60',
    [ExposureTime.OneHundredthOrOneOneHundredTwentieth]: '1/100 or 1/20',
    [ExposureTime.OneTwoHundredFiftieth]: '1/250',
    [ExposureTime.OneFiveHundredth]: '1/500',
    [ExposureTime.OneSevenHundredFiftieth]: '1/750',
    [ExposureTime.OneThousandth]: '1/1000',
    [ExposureTime.OneTwoThousandth]: '1/2000',
    [ExposureTime.OneFourThousandth]: '1/4000',
    [ExposureTime.OneTenThousandth]: '1/10000',
    [ExposureTime.OneHundredThousandth]: '1/100000',
    [ExposureTime.One]: '1',
};

const hlcModeMapping: { [key in HighLightCompensationMode]: string } = {
    [HighLightCompensationMode.Off]: 'Off',
    [HighLightCompensationMode.GeneralMode]: 'General',
    [HighLightCompensationMode.EnhanceMode]: 'Enhance',
};

const focusModeMapping: { [key in FocusMode]: string } = {
    [FocusMode.Auto]: 'Auto',
    [FocusMode.SemiAuto]: 'Semi Auto',
    [FocusMode.Manual]: 'Manual',
};

const zoomLimitMapping: { [key in ZoomLimit]: string } = {
    [ZoomLimit.MaxOpticalZoom]: 'Max Optical Zoom',
    [ZoomLimit.TwoMultiplyMaxOpticalZoom]: '2x Max Optical Zoom',
    [ZoomLimit.FourMultiplyMaxOpticalZoom]: '4x Max Optical Zoom',
    [ZoomLimit.EightMultiplyMaxOpticalZoom]: '8x Max Optical Zoom',
    [ZoomLimit.SixTeenMultiplyMaxOpticalZoom]: '16x Max Optical Zoom',
};

export const smartIrModeMapping: { [key in SmartIrMode]: string } = {
    [SmartIrMode.AutoMode]: 'Auto',
    [SmartIrMode.Customize]: 'Customize',
};

export const dnSwitchRefocusMapping: { [key in PTZCameraSettingEnable]: string } = {
    [PTZCameraSettingEnable.Off]: 'Off',
    [PTZCameraSettingEnable.On]: 'Dynamic',
};

const mirrorCorridorMapping: { [key in CorridorMode]: string } = {
    [CorridorMode.OFF]: 'Off',
    [CorridorMode.Clockwise90Degrees]: 'Clockwise 90°',
    [CorridorMode.Anticlockwise90Degrees]: 'Anticlockwise 90°',
};

const imageRotationMapping: { [key in ImageRotation]: string } = {
    [ImageRotation.OFF]: 'Off',
    [ImageRotation.Rotating180Degrees]: 'Rotating 180°',
    [ImageRotation.FlipHorizontal]: 'Flip Horizontal',
    [ImageRotation.FlipVertical]: 'Flip Vertical',
};

export const irCutFilterMapping: { [key in DayNightMode]: string } = {
    [DayNightMode.AutoMode]: 'Auto',
    [DayNightMode.NightMode]: 'Night',
    [DayNightMode.DayMode]: 'Day',
    [DayNightMode.Customize]: 'Customize',
};

const scheduleModeMapping: { [key in ImageEnhancementMode]: string } = {
    [ImageEnhancementMode.SingleMode]: 'Single',
    [ImageEnhancementMode.DayNightMode]: 'Day/Night',
    [ImageEnhancementMode.ScheduleMode]: 'Schedule',
};

const dayEnhancementModeMapping: { [key in EnhancementMode]: string } = {
    [EnhancementMode.BLC]: 'BLC',
    [EnhancementMode.WDR]: 'WDR',
    [EnhancementMode.HLC]: 'HLC',
    [EnhancementMode.Off]: 'Off',
};

enum LabelEnum {
    ExposureLevel = 'Exposure Level',
    MinimumShutter = 'Minimum Shutter',
    MaximumShutter = 'Maximum Shutter',
    LimitGainLevel = 'Limit Gain Level',
    IRCutLatency = 'IR-CUT Latency',
    IRCut = 'IR-CUT',
    IRLed = 'IR-LED',
    ColorMode = 'Color Mode',
}

const tableHeaders: TableHeaderStyle[] = [
    {
        label: 'No.',
        align: 'left',
    },
    {
        label: 'Name',
        align: 'left',
    },
    {
        label: 'Day',
        align: 'center',
    },
    {
        label: 'Night',
        align: 'center',
    },
];

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
};

export default function ImageGeneralMilesightsCameraPTZ(props: Props) {
    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;

    const configs = camera?.PTZCamera?.PTZCameraConfig;

    const PTZCameraDayNight = configs?.CameraDayNight;
    const PTZCameraSetting = configs?.CameraSetting;

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const statusList_ImageAdjustment: DataSectionHasSubItem[] = [
        { label: 'Brightness:', value: <Typography>{PTZCameraSetting?.Brightness}</Typography> },
        { label: 'Contrast:', value: <Typography>{PTZCameraSetting?.Contrast!}</Typography> },
        { label: 'Saturation:', value: <Typography>{PTZCameraSetting?.ColorSaturation!}</Typography> },
        { label: 'Sharpness:', value: <Typography>{PTZCameraSetting?.Sharpness!}</Typography> },
        { label: '2D DNR:', value: <Typography>{PTZCameraSetting?.Dnr2Level!}</Typography> },
        { label: '3D DNR:', value: <Typography>{PTZCameraSetting?.NfLevel!}</Typography> },
    ];

    const statusList_Day_NightSwitch: SectionHasSubItemProps[] = [
        {
            title: 'Day/Night switch',
            data: [
                {
                    label: 'Mode:',
                    value: <Typography>{irCutFilterMapping[PTZCameraSetting?.IrCutFilter!]}</Typography>,
                },
                { label: 'Day to night value:', value: <Typography>{PTZCameraSetting?.DayToNight!}</Typography> },
                { label: 'Night to day value:', value: <Typography>{PTZCameraSetting?.NightToDay!}</Typography> },
                {
                    label: 'Day/Night switch refocus:',
                    value: <Typography>{dnSwitchRefocusMapping[PTZCameraSetting?.DnSwitchRefocus!]}</Typography>,
                },
            ],
        },
        {
            title: 'Smart IR mode',
            data: [
                {
                    label: 'Mode:',
                    value: <Typography>{smartIrModeMapping[PTZCameraSetting?.SmartIrMode!]}</Typography>,
                },
                {
                    label: 'IR strength value:',
                    value: (
                        <Stack direction="row" spacing={1}>
                            <IrLevelDisplay
                                label="Near"
                                value={
                                    PTZCameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                        ? PTZCameraSetting?.NearViewIrLevel!
                                        : 0
                                }
                            />
                            <IrLevelDisplay
                                label="Mid"
                                value={
                                    PTZCameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                        ? PTZCameraSetting?.MiddleViewIrLevel!
                                        : 0
                                }
                            />
                            <IrLevelDisplay
                                label="Far"
                                value={
                                    PTZCameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                        ? PTZCameraSetting?.FarViewIrLevel!
                                        : 0
                                }
                            />
                        </Stack>
                    ),
                },
            ],
        },
    ];

    const statusList_Exposure: DataSectionHasSubItem[] = [
        { label: 'Mode:', value: <Typography>{exposureModeMapping[PTZCameraSetting?.ExposureMode!]}</Typography> },
        {
            label: 'Exposure time:',
            value: <Typography>{exposureTimeMapping[PTZCameraSetting?.ExposureTime!]}</Typography>,
        },
        { label: 'Gain level:', value: <Typography>{PTZCameraSetting?.ExposureGain!}</Typography> },
    ];

    const statusList_Backlight: DataSectionHasSubItem[] = [
        { label: 'Mode:', value: <Typography>{scheduleModeMapping[PTZCameraSetting?.ScheduleMode!]}</Typography> },
        {
            label: 'Backlight setting:',
            value: <Typography>{dayEnhancementModeMapping[PTZCameraSetting?.DayEnhancementMode!]}</Typography>,
        },
    ];

    const statusList_WhiteBalance: DataSectionHasSubItem[] = [
        { label: 'Mode:', value: <Typography>{hlcModeMapping[PTZCameraSetting?.HlcMode!]}</Typography> },
        {
            label: 'White balance:',
            value: <Typography>{whiteBalanceModeMapping[PTZCameraSetting?.WhiteBalanceMode!]}</Typography>,
        },
    ];

    const statusList_ImageEnhancement: DataSectionHasSubItem[] = [
        {
            label: 'IR balance mode:',
            value: <Typography>{irBlanceModeMapping[PTZCameraSetting?.IrBlanceMode!]}</Typography>,
        },
        {
            label: 'Reduce motion blur:',
            value: <Typography>{irBlanceModeMapping[PTZCameraSetting?.Enblur!]}</Typography>,
        },
        { label: 'Defog mode:', value: <Typography>{irBlanceModeMapping[PTZCameraSetting?.DefogMode!]}</Typography> },
    ];

    const statusList_Display: DataSectionHasSubItem[] = [
        {
            label: 'Power line frequency:',
            value: <Typography>{powerLineFrequencyMapping[PTZCameraSetting?.PowerlineFreq!]}</Typography>,
        },
        {
            label: 'Outdoor/Indoor mode:',
            value: <Typography>{doorModeMapping[PTZCameraSetting?.DoorMode!]}</Typography>,
        },
        {
            label: 'Corridor mode:',
            value: <Typography>{mirrorCorridorMapping[PTZCameraSetting?.MirrorCorridor!]}</Typography>,
        },
        {
            label: 'Image rotation:',
            value: <Typography>{imageRotationMapping[PTZCameraSetting?.ImageRotation!]}</Typography>,
        },
        { label: 'Zoom limit:', value: <Typography>{zoomLimitMapping[PTZCameraSetting?.ZoomLimit!]}</Typography> },
    ];

    const dayNightTableDataMapping = {
        [LabelEnum.ExposureLevel]: {
            dayValue: PTZCameraDayNight?.DayExposureLevel!,
            nightValue: PTZCameraDayNight?.NightExposureLevel!,
        },
        [LabelEnum.MinimumShutter]: {
            dayValue: exposureTimeMapping[PTZCameraDayNight?.DayMinShutter!],
            nightValue: exposureTimeMapping[PTZCameraDayNight?.NightMinShutter!],
        },
        [LabelEnum.MaximumShutter]: {
            dayValue: exposureTimeMapping[PTZCameraDayNight?.DayMaxShutter!],
            nightValue: exposureTimeMapping[PTZCameraDayNight?.NightMaxShutter!],
        },
        [LabelEnum.LimitGainLevel]: {
            dayValue: PTZCameraDayNight?.DayLimitGain!,
            nightValue: PTZCameraDayNight?.NightLimitGain!,
        },
        [LabelEnum.IRCutLatency]: {
            dayValue: PTZCameraDayNight?.DayIrCutInterval!,
            nightValue: PTZCameraDayNight?.NightIrCutInterval!,
        },
        [LabelEnum.IRCut]: {
            dayValue: irBlanceModeMapping[PTZCameraDayNight?.DayIrCutStatus!],
            nightValue: irBlanceModeMapping[PTZCameraDayNight?.NightIrCutStatus!],
        },
        [LabelEnum.IRLed]: {
            dayValue: irBlanceModeMapping[PTZCameraDayNight?.DayIrLedStatus!],
            nightValue: irBlanceModeMapping[PTZCameraDayNight?.NightIrLedStatus!],
        },
        [LabelEnum.ColorMode]: {
            dayValue: dayColorModeMapping[PTZCameraDayNight?.DayColorMode!],
            nightValue: dayColorModeMapping[PTZCameraDayNight?.NightColorMode!],
        },
    };

    const dayNightTableData = Object.values(LabelEnum).map((label, index) => ({
        id: index + 1,
        label,
        dayValue: dayNightTableDataMapping[label].dayValue,
        nightValue: dayNightTableDataMapping[label].nightValue,
    }));

    return (
        <Stack id="imageAdjustment" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Video */}
            <SectionHasSubItemHeader id="" title="Image general" onEdit={handleEdit} />

            <SectionNotSubItem
                header="Image Adjustment"
                statusList={statusList_ImageAdjustment}
                isOpen={isOpenCollapse.imageAdjustment}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.ImageAdjustment);
                }}
            />

            <BaseSectionCollapse
                id="day_nightSwitch"
                title="Day/Night switch"
                isOpen={isOpenCollapse.day_nightSwitch}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.DayNightSwitch);
                }}
                sections={statusList_Day_NightSwitch}
            />

            <SectionNotSubItem
                name="day_nightParameters"
                header="Day/Night parameters"
                tableHeaders={tableHeaders}
                items={dayNightTableData}
                tableBody={
                    <TableBody>
                        {dayNightTableData.map((row) => (
                            <TableRow key={row.id}>
                                <CellOfRow value={row.id} align="left" />
                                <CellOfRow value={row.label} align="left" />
                                <CellOfRow value={row.dayValue} align="center" />
                                <CellOfRow value={row.nightValue} align="center" />
                            </TableRow>
                        ))}
                    </TableBody>
                }
                isOpen={isOpenCollapse.day_nightParameters}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.DayNightParameters);
                }}
            />
            <SectionNotSubItem
                name="exposure"
                header="Exposure"
                statusList={statusList_Exposure}
                isOpen={isOpenCollapse.exposure}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.Exposure);
                }}
            />
            <SectionNotSubItem
                name="backlight"
                header="Backlight"
                statusList={statusList_Backlight}
                isOpen={isOpenCollapse.backlight}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.Backlight);
                }}
            />
            <SectionNotSubItem
                name="whiteBalance"
                header="White balance"
                statusList={statusList_WhiteBalance}
                isOpen={isOpenCollapse.whiteBalance}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.WhiteBalance);
                }}
            />
            <SectionNotSubItem
                name="imageEnhancement"
                header="Image enhancement"
                statusList={statusList_ImageEnhancement}
                isOpen={isOpenCollapse.imageEnhancement}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.ImageEnhancement);
                }}
            />
            <SectionNotSubItem
                name="display"
                header="Display"
                statusList={statusList_Display}
                isOpen={isOpenCollapse.display}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.Display);
                }}
            />
        </Stack>
    );
}

interface IrLevelProps {
    label: string;
    value: number;
}

export const IrLevelDisplay = ({ label, value }: IrLevelProps) => (
    <Stack direction="row" alignItems="center">
        <Typography>{label}:</Typography>
        <Typography fontWeight={500} variant="h6">
            {value}
        </Typography>
    </Stack>
);
